'use client';

import type { ComponentVariant } from '../types';
import { Button } from '@florencelabs/button';
import { MaterialSymbol } from '@florencelabs/icons';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { removeRFVSuffix } from '@provider-directory/components/availability/availability.model';
import { formatPhone } from '@provider-directory/components/phone-number';
import type { NoAvailabilityScenarioType } from '@provider-directory/contexts/NoAvailabilityScenarioContext';
import { useAvailabilityWindow } from '@provider-directory/hooks/useAvailabilityWindow';

export interface NoAvailabilityBannerProps {
  reasonForVisit?: string | null;
  type: NoAvailabilityScenarioType;
  locationPhoneNumber?: string | null;
  onNavigateToSlot?: () => void;
  variant?: ComponentVariant;
}

export const NoAvailabilityBanner = ({
  reasonForVisit,
  type,
  locationPhoneNumber,
  onNavigateToSlot,
  variant = 'system',
}: NoAvailabilityBannerProps): ReactElement | null => {
  const { t } = useTranslation();
  const router = useRouter();
  const maxDays = useAvailabilityWindow();

  const handleViewOtherProviders = (includeRfv = true) => {
    const url = new URL('/providers/find', window.location.origin);
    if (includeRfv && reasonForVisit) {
      url.searchParams.set('reason-for-visit', reasonForVisit);
    }

    router.push(url.toString());
  };

  const message = useMemo(() => {
    if (!reasonForVisit) {
      return { message: null, ctaButton: null };
    }

    // TODO: use correct rfv here once cached slots v1 removal is merged DIR-1878 (to avoid conflicts)
    const reasonForVisitLabel = t(
      `decisionTree.reasonForVisit.${removeRFVSuffix(reasonForVisit)}.label`,
    );

    switch (type) {
      case 'date':
        return {
          message: t('availability.noSlotsForDate', {
            reasonForVisit: reasonForVisitLabel,
          }),
          ctaButton: t('availability.viewNextAvailableAtThisLocation'),
        };
      case 'location':
        return {
          message: t('availability.noSlotsForLocation'),
          ctaButton: t('availability.viewNextAvailableAtAnotherLocation'),
        };
      default:
        return {
          message: locationPhoneNumber
            ? t('availability.noSlotsAvailable', {
                reasonForVisit: reasonForVisitLabel,
                phoneNumber:
                  formatPhone(locationPhoneNumber) || locationPhoneNumber,
                maxDays,
              })
            : t('availability.noSlotsAvailableNoPhoneNumber', {
                reasonForVisit: reasonForVisitLabel,
                maxDays,
              }),
          ctaButton: t('availability.viewOtherAvailableProviders'),
        };
    }
  }, [reasonForVisit, t, type, locationPhoneNumber, maxDays]);

  if (!message.message) {
    return null;
  }

  const ctaHandler: Record<
    NoAvailabilityScenarioType,
    (() => void) | undefined
  > = {
    date: onNavigateToSlot,
    location: onNavigateToSlot,
  } as Record<NoAvailabilityScenarioType, (() => void) | undefined>;
  ctaHandler['other-providers'] = () => {
    handleViewOtherProviders();
  };
  ctaHandler['no-providers'] = () => {
    handleViewOtherProviders(false);
  };

  return (
    <div
      data-testid={'no-availability-banner'}
      className={clsx(
        'mb-4 flex flex-col justify-between gap-y-4 rounded-sm p-6',
        variant === 'tonal' ? 'bg-primary-200' : 'bg-black/5',
      )}
    >
      <p className={'text-body-small'}>{message.message}</p>

      {message.ctaButton && ctaHandler[type] && (
        <Button
          testId={'no-availability-banner-cta'}
          variant={'systemLink'}
          onPress={ctaHandler[type]}
          className={'flex gap-2 text-left text-[14px] font-bold'}
        >
          {message.ctaButton}
          <MaterialSymbol name={'arrow_forward'} />
        </Button>
      )}
    </div>
  );
};
